import { Form, Link, useLocation, useParams, useSubmit } from '@remix-run/react'
import React, { useState } from 'react'
import { Icon } from '#app/components/ui/icon.tsx'
import type { IconName } from '#app/components/ui/icon.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu.tsx'
import { cn } from '#app/utils/misc.tsx'
import { LogOut } from 'lucide-react'

export type NavType = {
	name: string
	href: string
	icon: IconName
}

export const secondaryNavigation: NavType[] = [
	{ 
		name: 'Personal Info', 
		href: 'edit/personal-info',
		icon: 'user'
	},
	{ 
		name: 'Player Info', 
		href: 'edit/player-info',
		icon: 'user-square'
	},
	{ 
		name: 'Social Info', 
		href: 'edit/social-info',
		icon: 'share'
	},
	{ 
		name: 'Security', 
		href: 'edit/security',
		icon: 'shield'
	},
]

const PlayerNav = ({ isOwner }: { isOwner: boolean }) => {
	const { pathname } = useLocation()
	const { playerId } = useParams<{ playerId: string }>()
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
	const submit = useSubmit()

	if (!isOwner) return null

	return (
		<nav className="flex h-full w-full items-center justify-end">
			{/* Desktop Navigation */}
			<div className="hidden lg:flex lg:items-center lg:gap-6">
				<div className="rounded-md bg-background-dark-foreground/60 p-0.5 backdrop-blur-sm">
					<ul className="flex items-center">
						{secondaryNavigation.map((nav) => (
							<li key={nav.href}>
								<Link
									to={`/player/${playerId}/${nav.href}`}
									className={cn(
										'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-200',
										{
											'bg-background-dark text-white/90': pathname.includes(nav.href),
											'text-white/70 hover:text-white hover:bg-background-dark/70': !pathname.includes(nav.href),
										},
									)}
								>
									<Icon 
										name={nav.icon} 
										className={cn(
											'size-4 transition-colors duration-200',
											pathname.includes(nav.href) ? 'text-primary' : 'text-white/60 group-hover:text-primary'
										)}
									/>
									{nav.name}
								</Link>
							</li>
						))}
					</ul>
				</div>

				{/* User Menu */}
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<button className="flex items-center gap-2 rounded-md bg-background-dark-foreground/60 px-3 py-1.5 text-white/90 transition-colors hover:bg-background-dark">
							<span className="font-medium">Player Menu</span>
							<Icon name="chevron-down" className="size-4" />
						</button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end" className="w-56 bg-background-dark-foreground border-background-dark">
						{secondaryNavigation.map((nav) => (
							<DropdownMenuItem key={nav.href} asChild>
								<Link 
									to={`/player/${playerId}/${nav.href}`}
									className="flex items-center gap-2 text-white/80 hover:text-white"
								>
									<Icon name={nav.icon} className="size-4 text-white/60" />
									{nav.name}
								</Link>
							</DropdownMenuItem>
						))}
						<DropdownMenuSeparator className="bg-background-dark/60" />
						<DropdownMenuItem 
							className="text-red-400 focus:text-white focus:bg-red-600"
							onClick={(e) => {
								e.preventDefault()
								submit(e.currentTarget.closest('form'))
							}}
						>
							<Form
								className="flex w-full items-center gap-2"
								action="/logout"
								method="POST"
							>
								<LogOut className="size-4" />
								<span>Sign out</span>
							</Form>
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>

			{/* Mobile Menu Button */}
			<button
				className="rounded-md bg-background-dark-foreground/60 p-1.5 text-white/70 hover:text-white lg:hidden"
				onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
			>
				<Icon name="menu" className="size-5" />
			</button>

			{/* Mobile Menu */}
			{isMobileMenuOpen && (
				<div className="fixed inset-0 z-[100] lg:hidden">
					<div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
					<div className="absolute inset-y-0 right-0 w-full max-w-sm bg-background-dark shadow-xl">
						<div className="flex h-16 items-center justify-between border-b border-background-dark-foreground/30 px-4">
							<h2 className="text-base font-semibold text-white/90">Player Settings</h2>
							<button
								onClick={() => setIsMobileMenuOpen(false)}
								className="rounded-md bg-background-dark-foreground/50 p-1.5 text-white/70 hover:text-white"
							>
								<Icon name="close" className="size-4" />
							</button>
						</div>

						<div className="h-[calc(100vh-4rem)] overflow-y-auto p-4">
							<ul className="space-y-1">
								{secondaryNavigation.map((nav) => (
									<li key={nav.href}>
										<Link
											to={`/player/${playerId}/${nav.href}`}
											onClick={() => setIsMobileMenuOpen(false)}
											className={cn(
												'flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md',
												{
													'bg-background-dark-foreground text-white/90': pathname.includes(nav.href),
													'text-white/70 hover:bg-background-dark-foreground/70 hover:text-white/90': !pathname.includes(nav.href),
												},
											)}
										>
											<Icon 
												name={nav.icon} 
												className={cn(
													'size-4 transition-colors duration-200',
													pathname.includes(nav.href) ? 'text-primary' : 'text-white/60'
												)}
											/>
											{nav.name}
										</Link>
									</li>
								))}
							</ul>

							<div className="mt-4 border-t border-background-dark-foreground/30 pt-4">
								<Form
									action="/logout"
									method="POST"
									className="w-full"
									onSubmit={() => setIsMobileMenuOpen(false)}
								>
									<button
										type="submit"
										className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-background-dark-foreground/50 hover:text-red-300"
									>
										<LogOut className="size-4" />
										Sign out
									</button>
								</Form>
							</div>
						</div>
					</div>
				</div>
			)}
		</nav>
	)
}

export default PlayerNav
